/**
 * The sidebar mascot. Purely presentational — mood and label are computed in
 * the App shell from data it already loads. Styles live in src/index.css
 * under "sidebar mascot"; animations freeze under prefers-reduced-motion.
 *
 * The character itself is <MascotFigure>, shared with the sign-in screen so
 * there is exactly one bunny in the codebase. Everything that moves is driven
 * by classes on an ancestor (.pet here, .login-mascot there), so each host
 * decides its own mood and animation without touching this file.
 */

export type MascotMood = 'happy' | 'sad'

/**
 * The bunny on its own, with no layout or mood opinion.
 *
 * `gradientId` exists because SVG ids are document-global: two figures on one
 * page sharing `url(#…)` would both resolve to whichever rendered first.
 */
export function MascotFigure({
  className = 'guy',
  gradientId = 'mascot-fur',
}: {
  className?: string
  gradientId?: string
}) {
  const fur = `url(#${gradientId})`
  return (
    <svg className={className} viewBox="0 0 120 120" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#7d7aea" />
          <stop offset="1" stopColor="#5b5bd6" />
        </linearGradient>
      </defs>
      <ellipse cx="40" cy="24" rx="8" ry="16" fill={fur} transform="rotate(-16 40 24)" />
      <ellipse cx="80" cy="24" rx="8" ry="16" fill={fur} transform="rotate(16 80 24)" />
      <ellipse cx="41" cy="26" rx="3.4" ry="8" fill="#c9c7fb" transform="rotate(-16 41 26)" />
      <ellipse cx="79" cy="26" rx="3.4" ry="8" fill="#c9c7fb" transform="rotate(16 79 26)" />
      <ellipse cx="60" cy="70" rx="38" ry="34" fill={fur} />
      <ellipse cx="60" cy="80" rx="24" ry="18" fill="#fff" opacity=".15" />
      <circle cx="47" cy="62" r="11" fill="#fff" />
      <circle cx="73" cy="62" r="11" fill="#fff" />
      <g className="pupils">
        <circle cx="48" cy="61" r="5.2" fill="#241f52" />
        <circle cx="74" cy="61" r="5.2" fill="#241f52" />
        <circle className="spark" cx="45.6" cy="59" r="1.8" fill="#fff" />
        <circle className="spark" cx="71.6" cy="59" r="1.8" fill="#fff" />
      </g>
      <circle className="cheek" cx="38" cy="74" r="5" fill="#ff9ecb" opacity="0" />
      <circle className="cheek" cx="82" cy="74" r="5" fill="#ff9ecb" opacity="0" />
      <path
        className="m-happy"
        d="M51 82 Q60 93 69 82"
        stroke="#241f52"
        strokeWidth="3.2"
        fill="none"
        strokeLinecap="round"
      />
      <path
        className="m-sad"
        d="M51 88 Q60 80 69 88"
        stroke="#241f52"
        strokeWidth="3.2"
        fill="none"
        strokeLinecap="round"
      />
      <path className="tear" d="M75 68 q-3.2 6 0 8.4 q3.2 -2.4 0 -8.4Z" fill="#7fb8ff" />
      <ellipse cx="49" cy="103" rx="7" ry="4" fill="#4a46c6" />
      <ellipse cx="71" cy="103" rx="7" ry="4" fill="#4a46c6" />
    </svg>
  )
}

export function Mascot({ mood, label }: { mood: MascotMood; label: string }) {
  return (
    <div className={`pet ${mood}`}>
      <div className="stage">
        <span className="env a" />
        <span className="env b" />
        <span className="env c" />
        <MascotFigure gradientId="pet-fur" />
      </div>
      <div className="mlabel">
        <span className="ld" />
        <span>{label}</span>
      </div>
    </div>
  )
}
